import React from 'react';
import { Activity, Award } from 'lucide-react';

/**
 * Компонент выбора типа тренировки и уровня физической подготовки
 */
const ExerciseTypeSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  exerciseTypes,
  fitnessLevels
}) => {
  return (
    <div className="mb-6 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Activity className="w-5 h-5 mr-2 text-red-500" />
        Тип тренировки и физическая подготовка
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Уровень физической подготовки */}
        <div>
          <label htmlFor="fitnessLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Award className="w-4 h-4 mr-2 text-red-500" />
            Уровень физической подготовки
          </label>
          <select
            id="fitnessLevel"
            name="fitnessLevel"
            value={formData.fitnessLevel}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.fitnessLevel ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите уровень...</option>
            {fitnessLevels.map((level) => (
              <option key={level.id} value={level.id}>
                {level.name} {level.adjustment !== 0 ? `(${level.adjustment > 0 ? '+' : ''}${level.adjustment}%)` : ''}
              </option>
            ))}
          </select>
          {errors.fitnessLevel && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.fitnessLevel}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Уровень подготовки влияет на ваш максимальный пульс и рекомендуемые зоны.
          </p>
        </div>

        {/* Тип тренировки */}
        <div>
          <label htmlFor="exerciseType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Activity className="w-4 h-4 mr-2 text-red-500" />
            Цель тренировки
          </label>
          <select
            id="exerciseType"
            name="exerciseType"
            value={formData.exerciseType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.exerciseType ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите тип...</option>
            {exerciseTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} ({type.defaultZones.lower}-{type.defaultZones.upper}%)
              </option>
            ))}
          </select>
          {errors.exerciseType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.exerciseType}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Тип тренировки определяет оптимальную зону пульса для достижения ваших целей.
          </p>
        </div>
      </div>

      {/* Информация о выбранном типе тренировки */}
      {formData.exerciseType && (
        <div className="mt-4 bg-white dark:bg-gray-700 p-3 rounded-lg border border-red-100 dark:border-red-700 text-sm">
          <div className="flex items-start">
            <Activity className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-800 dark:text-white mb-1">
                {exerciseTypes.find(t => t.id === formData.exerciseType)?.name || 'Выбранный тип тренировки'}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {formData.exerciseType === 'cardio' && 'Классическая кардиотренировка направлена на укрепление сердечно-сосудистой системы. Оптимальная зона 70-85% от максимального пульса обеспечивает эффективное развитие кардиосистемы без чрезмерной нагрузки.'}
                {formData.exerciseType === 'weight_loss' && 'Тренировка для снижения веса наиболее эффективна в зоне 60-70% от максимального пульса. В этой зоне организм использует жиры как основной источник энергии, что способствует эффективному жиросжиганию.'}
                {formData.exerciseType === 'endurance' && 'Тренировка на выносливость предполагает работу в зоне 75-85% от максимального пульса. Эта зона оптимальна для увеличения объема легких, повышения эффективности работы сердца и развития выносливости.'}
                {formData.exerciseType === 'recovery' && 'Восстановительная тренировка проходит в зоне 50-60% от максимального пульса. Такая низкоинтенсивная нагрузка помогает улучшить кровообращение и ускорить процесс восстановления после интенсивных тренировок.'}
                {formData.exerciseType === 'interval' && 'Интервальная тренировка предполагает чередование периодов высокой (85-90%) и низкой (60-70%) интенсивности. Такой подход эффективен для развития как аэробной, так и анаэробной выносливости.'}
                {formData.exerciseType === 'strength' && 'Силовая тренировка с кардионагрузкой проходит в зоне 70-80% от максимального пульса. Эта зона обеспечивает достаточную интенсивность для укрепления мышц, не перегружая сердечно-сосудистую систему.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseTypeSection;